import Phaser from 'phaser'
import { COLORS, Difficulty, PEARL_COLORS } from '../config'

export class MenuScene extends Phaser.Scene {
  private selectedDifficulty: Difficulty = 'easy'
  private startPrompt!: Phaser.GameObjects.Text
  private thea!: Phaser.GameObjects.Sprite
  private bubbles: Phaser.GameObjects.Arc[] = []
  private difficultyButtons: Phaser.GameObjects.Container[] = []
  private floatingPearls: Phaser.GameObjects.Sprite[] = []

  constructor() {
    super({ key: 'MenuScene' })
  }

  create() {
    const { width, height } = this.cameras.main
    
    // Get stored difficulty or default
    this.selectedDifficulty = this.registry.get('difficulty') || 'easy'
    
    // Create animated underwater background
    this.createBackground()
    
    // Floating bubbles
    this.createBubbles()
    
    // Floating decorative pearls
    this.createFloatingPearls()
    
    // Title
    this.add.text(width / 2, 70, '🦪 Pearl Pop 🦪', {
      fontFamily: 'Georgia, serif',
      fontSize: '48px',
      color: '#B8F0E8',
      stroke: '#1E3A5F',
      strokeThickness: 4,
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: '#1E3A5F',
        blur: 4,
        fill: true
      }
    }).setOrigin(0.5)
    
    // Subtitle
    this.add.text(width / 2, 120, 'Match pearls to make them pop!', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '18px',
      color: '#98E8DC'
    }).setOrigin(0.5)
    
    // Thea preview (animated) - on the left side
    this.thea = this.add.sprite(100, height / 2 + 20, 'thea')
    this.thea.setScale(0.25)
    
    // Gentle bobbing animation
    this.tweens.add({
      targets: this.thea,
      y: this.thea.y - 15,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    })
    
    // Slight rotation
    this.tweens.add({
      targets: this.thea,
      angle: 5,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    })
    
    // How to play section
    this.createHowToPlay()
    
    // Difficulty selector
    this.createDifficultySelector()
    
    // Start prompt (pulsing)
    this.startPrompt = this.add.text(width / 2, height - 80, 'Tap or Click to Start!', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#FF9966',
      stroke: '#1E3A5F',
      strokeThickness: 2
    }).setOrigin(0.5)
    
    this.tweens.add({
      targets: this.startPrompt,
      alpha: 0.5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    })
    
    // High score display
    const highScore = this.registry.get('highScore') || 0
    if (highScore > 0) {
      this.add.text(width / 2, height - 40, `🏆 Best Score: ${highScore}`, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        color: '#F4A460'
      }).setOrigin(0.5)
    }
    
    // Input handlers
    this.input.on('pointerdown', this.handlePointerDown, this)
    this.input.keyboard?.on('keydown-SPACE', this.startGame, this)
  }

  shutdown() {
    // Clean up input handlers when leaving this scene
    this.input.off('pointerdown', this.handlePointerDown, this)
    this.input.keyboard?.off('keydown-SPACE', this.startGame, this)
  }

  handlePointerDown = (pointer: Phaser.Input.Pointer) => {
    // Check if clicking on a button - if not, start game
    const hitObjects = this.input.hitTestPointer(pointer)
    const clickedOnButton = hitObjects.some(obj => 
      obj instanceof Phaser.GameObjects.Container
    )
    
    if (!clickedOnButton) {
      this.startGame()
    }
  }

  createBackground() {
    const { width, height } = this.cameras.main
    
    // Gradient background (deep to light)
    const bg = this.add.graphics()
    
    // Create vertical gradient effect - ocean colors
    for (let y = 0; y < height; y += 2) {
      const ratio = y / height
      // Dark navy at top, lighter teal at bottom
      const r = Math.floor(30 + (64 - 30) * ratio)
      const g = Math.floor(58 + (181 - 58) * ratio)
      const b = Math.floor(95 + (168 - 95) * ratio)
      bg.fillStyle(Phaser.Display.Color.GetColor(r, g, b), 1)
      bg.fillRect(0, y, width, 2)
    }
    
    // Add some coral/seaweed silhouettes at bottom
    const coralColors = [0x2D5A5A, 0x3D6A6A, 0x4D7A7A]
    for (let i = 0; i < 8; i++) {
      const coral = this.add.graphics()
      coral.fillStyle(coralColors[i % 3], 0.3)
      const x = i * 80 + Math.random() * 30
      const h = 30 + Math.random() * 50
      coral.fillRoundedRect(x, height - h, 25 + Math.random() * 35, h, 8)
      coral.fillCircle(x + 15, height - h, 12 + Math.random() * 8)
    }
  }

  createBubbles() {
    const { width, height } = this.cameras.main
    
    // Create floating bubbles
    for (let i = 0; i < 12; i++) {
      const x = Math.random() * width
      const y = height + Math.random() * 100
      const size = 3 + Math.random() * 6
      
      const bubble = this.add.circle(x, y, size, COLORS.iceAqua, 0.25)
      this.bubbles.push(bubble)
      
      // Animate bubble rising
      this.tweens.add({
        targets: bubble,
        y: -20,
        x: bubble.x + (Math.random() - 0.5) * 80,
        duration: 4000 + Math.random() * 4000,
        repeat: -1,
        delay: Math.random() * 3000,
        onRepeat: () => {
          bubble.x = Math.random() * width
          bubble.y = height + 20
        }
      })
    }
  }

  createFloatingPearls() {
    const { width } = this.cameras.main
    
    // Create some decorative floating pearls around the edges
    const positions = [
      { x: width - 60, y: 180 },
      { x: width - 100, y: 280 },
      { x: width - 50, y: 380 },
      { x: width - 90, y: 480 }
    ]
    
    positions.forEach((pos, i) => {
      const colorIndex = i % PEARL_COLORS.length
      const pearl = this.add.sprite(pos.x, pos.y, `pearl_${PEARL_COLORS[colorIndex].name}`)
      pearl.setScale(1.2)
      this.floatingPearls.push(pearl)
      
      // Gentle float animation
      this.tweens.add({
        targets: pearl,
        y: pos.y - 10 - Math.random() * 10,
        duration: 1200 + Math.random() * 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: i * 200
      })
      
      // Subtle rotation
      this.tweens.add({
        targets: pearl,
        angle: { from: -5, to: 5 },
        duration: 2000 + Math.random() * 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      })
    })
  }

  createHowToPlay() {
    const { width, height } = this.cameras.main
    
    // Instructions box
    const boxX = width / 2 - 10
    const boxY = height / 2 - 60
    const boxWidth = 280
    const boxHeight = 140
    
    const instructionsBg = this.add.graphics()
    instructionsBg.fillStyle(COLORS.darkNavy, 0.7)
    instructionsBg.fillRoundedRect(boxX - boxWidth / 2, boxY - boxHeight / 2, boxWidth, boxHeight, 12)
    instructionsBg.lineStyle(2, COLORS.mediumTeal, 0.5)
    instructionsBg.strokeRoundedRect(boxX - boxWidth / 2, boxY - boxHeight / 2, boxWidth, boxHeight, 12)
    
    this.add.text(boxX, boxY - 50, 'How to Play', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '18px',
      color: '#5DD3C3',
      fontStyle: 'bold'
    }).setOrigin(0.5)
    
    const instructions = [
      '🎯 Drag back to aim, release to shoot',
      '💎 Match 3+ same-color pearls to pop',
      '🌊 Orphan pearls fall for bonus points',
      '⚠️ Don\'t let pearls reach the bottom!'
    ]
    
    instructions.forEach((text, i) => {
      this.add.text(boxX, boxY - 20 + i * 24, text, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#B8F0E8'
      }).setOrigin(0.5)
    })
  }

  createDifficultySelector() {
    const { width, height } = this.cameras.main
    const difficulties: Difficulty[] = ['easy', 'medium', 'hard']
    const buttonWidth = 100
    const spacing = 15
    const totalWidth = difficulties.length * buttonWidth + (difficulties.length - 1) * spacing
    const startX = (width - totalWidth) / 2 + buttonWidth / 2
    
    this.add.text(width / 2, height / 2 + 80, 'Select Difficulty:', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#98E8DC'
    }).setOrigin(0.5)
    
    difficulties.forEach((diff, index) => {
      const x = startX + index * (buttonWidth + spacing)
      const y = height / 2 + 125
      
      const container = this.add.container(x, y)
      
      // Button background
      const bg = this.add.graphics()
      const isSelected = diff === this.selectedDifficulty
      bg.fillStyle(isSelected ? COLORS.lightTeal : COLORS.darkNavy, isSelected ? 1 : 0.8)
      bg.lineStyle(2, COLORS.lightTeal)
      bg.fillRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
      bg.strokeRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
      
      // Button text
      const label = diff.charAt(0).toUpperCase() + diff.slice(1)
      const text = this.add.text(0, 0, label, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        color: isSelected ? '#1E3A5F' : '#B8F0E8'
      }).setOrigin(0.5)
      
      container.add([bg, text])
      container.setSize(buttonWidth, 40)
      container.setInteractive({ useHandCursor: true })
      
      container.on('pointerover', () => {
        if (diff !== this.selectedDifficulty) {
          bg.clear()
          bg.fillStyle(COLORS.mediumTeal, 0.6)
          bg.lineStyle(2, COLORS.lightTeal)
          bg.fillRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
          bg.strokeRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
        }
      })
      
      container.on('pointerout', () => {
        if (diff !== this.selectedDifficulty) {
          bg.clear()
          bg.fillStyle(COLORS.darkNavy, 0.8)
          bg.lineStyle(2, COLORS.lightTeal)
          bg.fillRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
          bg.strokeRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
        }
      })
      
      container.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation()
        this.selectDifficulty(diff)
      })
      
      this.difficultyButtons.push(container)
    })
  }

  selectDifficulty(diff: Difficulty) {
    this.selectedDifficulty = diff
    this.registry.set('difficulty', diff)
    
    // Update button visuals
    const difficulties: Difficulty[] = ['easy', 'medium', 'hard']
    const buttonWidth = 100
    
    this.difficultyButtons.forEach((container, index) => {
      const bg = container.getAt(0) as Phaser.GameObjects.Graphics
      const text = container.getAt(1) as Phaser.GameObjects.Text
      const isSelected = difficulties[index] === diff
      
      bg.clear()
      bg.fillStyle(isSelected ? COLORS.lightTeal : COLORS.darkNavy, isSelected ? 1 : 0.8)
      bg.lineStyle(2, COLORS.lightTeal)
      bg.fillRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
      bg.strokeRoundedRect(-buttonWidth / 2, -20, buttonWidth, 40, 10)
      
      text.setColor(isSelected ? '#1E3A5F' : '#B8F0E8')
    })
  }

  startGame = () => {
    // Store the difficulty for the game scene
    this.registry.set('difficulty', this.selectedDifficulty)
    this.scene.start('GameScene')
  }
}
